import React, { useEffect, useState, useRef, useCallback } from "react";
import { addAdmission, deleteAdmission, approveAdmission } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import { useGlobalLoader } from "../../context/GlobalLoaderContext";
import "../superadmin/AdminPanel.css";

const createInitialForm = () => ({
  title: "",
  slug: ""
});

function HrAdmissionAdminPanel({ darkMode }) {
  const { threeTable, fetchThreeTableSection } = useGlobalData();
  const [admissions, setAdmissions] = useState([]);
  const [admissionsOffset, setAdmissionsOffset] = useState(0);
  const [admissionsHasMore, setAdmissionsHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const [formData, setFormData] = useState(createInitialForm());
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const role = localStorage.getItem("role");
  const { loading: globalLoading } = useGlobalLoader();

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      // No need to set isLoading
      await fetchThreeTableSection("admissions", false, { limit: PAGE_LIMIT, offset: 0 });
      if (!ignore) setIsLoading(false);
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line
  }, []);

  // Sync admissions from global state
  useEffect(() => {
    setAdmissions(threeTable.admissions || []);
    setAdmissionsOffset(threeTable.admissions?.length || 0);
    setAdmissionsHasMore((threeTable.admissions?.length || 0) === PAGE_LIMIT);
    setIsLoading(false);
  }, [threeTable.admissions]);

  // Infinite scroll fetch
  const fetchMoreAdmissions = useCallback(async () => {
    if (!admissionsHasMore || isFetchingMore) return;
    setIsFetchingMore(true);
    await fetchThreeTableSection("admissions", false, { limit: PAGE_LIMIT, offset: admissionsOffset });
    setIsFetchingMore(false);
  }, [admissionsHasMore, isFetchingMore, admissionsOffset, fetchThreeTableSection]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!admissionsHasMore || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreAdmissions();
    }
  }, [admissionsHasMore, isFetchingMore, fetchMoreAdmissions]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      const title = formData.title.trim();
      const slug = formData.slug.trim();
      if (!title) {
        throw new Error("Title required hai.");
      }
      if (!slug) {
        throw new Error("Slug required hai.");
      }
      await addAdmission({ title, slug });
      setMessage("Admission added!");
      setFormData(createInitialForm());
      await fetchThreeTableSection("admissions", true, { limit: PAGE_LIMIT, offset: 0 });
    } catch (err) {
      setError(err.message || "Unable to add admission");
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveAdmission(id);
      setMessage("Admission approved!");
      await fetchThreeTableSection("admissions", true, { limit: PAGE_LIMIT, offset: 0 });
    } catch (err) {
      setError(err.message || "Unable to approve admission");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this admission?")) {
      try {
        await deleteAdmission(id);
        setMessage("Admission deleted!");
        await fetchThreeTableSection("admissions", true, { limit: PAGE_LIMIT, offset: 0 });
      } catch (err) {
        setError(err.message || "Unable to delete admission");
      }
    }
  };

  if (role !== "hr") {
    return <div className="admin-panel-error">❌ Access denied! Only HR can access this panel.</div>;
  }

  return (
    <div className={`resource-admin-panel resource-admin-panel--purple${darkMode ? " dark-mode" : ""}`}>
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">HR Admission Admin Panel</h2>
          <p className="resource-admin-panel__subtext">Yahan se admission records ko add, review aur delete kar sakte hain. Title aur slug required hai.</p>
        </div>
        <div className="resource-admin-panel__pill">Admission</div>
      </div>
      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label>Title:</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>
        <div className="resource-admin-panel__field">
          <label>Slug:</label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            required
          />
        </div>
        <button type="submit">Add Admission</button>
      </form>
      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}
      <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
        <h3>Admission Entries</h3>
        {globalLoading ? (
          <div className="global-loader"><div className="skeleton-loader" /></div>
        ) : (
          <ul>
            {admissions.map((admission) => {
              const isPending = admission.status === "pending";
              const statusColor = isPending ? "#f7b500" : "#2ecc40";
              return (
                <li key={admission._id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span>{admission.title} ({admission.slug})</span>
                  <span style={{
                    background: statusColor,
                    color: "#fff",
                    borderRadius: 6,
                    padding: "2px 10px",
                    fontWeight: 600,
                    fontSize: 13
                  }}>{admission.status === "pending" ? "Pending" : "Approved"}</span>
                  {isPending && (
                    <>
                      <button onClick={() => handleApprove(admission._id)} style={{ background: "#f7b500", color: "#fff", marginLeft: 8 }}>Approve</button>
                      <button onClick={() => handleDelete(admission._id)} style={{ marginLeft: 8 }}>Delete</button>
                    </>
                  )}
                  {!isPending && null}
                </li>
              );
            })}
          </ul>
        )}
        {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
        {!admissionsHasMore && !isLoading && admissions.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more admissions.</div>}
      </div>
    </div>
  );
}

export default HrAdmissionAdminPanel;
